import React, { useMemo, useState } from "react";
import {AbsoluteFill, OffthreadVideo, staticFile} from "remotion";
import {parseMedia} from "@remotion/media-utils";
import { z } from "zod";

// Simplified Zod schema for TwoUpSplitYoutube props
const TwoUpSplitYoutubePropsSchema = z.object({
  caption: z.string(),
  topSrc: z.string(),
  bottomYoutubeUrl: z.string(),
  youtubeStartTime: z.string().optional(), // Accepts "MM:SS" format
  youtubeEndTime: z.string().optional(),   // Accepts "MM:SS" format
  downloadedVideoPath: z.string().optional(), // Path to downloaded MP4
  useDownloadedVideo: z.boolean().default(true), // Whether to use downloaded video
  split: z.number(),
  topMuted: z.boolean(),
  borderColor: z.string(),
  borderWidth: z.number(),
  objectFit: z.string(),
  captionBgColor: z.string(),
  captionTextColor: z.string(),
  captionPadding: z.string(),
  captionFontSize: z.string(),
  captionFontWeight: z.string(),
  captionFontFamily: z.string(),
  captionBorderRadius: z.string()
});

export { TwoUpSplitYoutubePropsSchema };

/**
 * Calculate metadata for TwoUpSplitYoutube composition
 * - Dynamically sets duration based on topSrc video length
 */
export const calculateMetadata = async ({ props, defaultProps }) => {
  const { topSrc } = { ...defaultProps, ...props };
  
  try {
    // Get video metadata from the top source using parseMedia
    const metadata = await parseMedia({
      src: topSrc,
      fields: {
        durationInSeconds: true,
      }
    });
    
    // Calculate duration in frames based on video duration
    // Use 30 fps as default, but you can also get this from composition config
    const fps = 30;
    const durationInFrames = Math.ceil(metadata.durationInSeconds * fps);
    
    return {
      durationInFrames,
    };
  } catch (error) {
    console.warn('Could not get video metadata, using default duration:', error);
    // Fallback to default duration if metadata fails
    return {
      durationInFrames: 1050, // Default fallback
    };
  }
};

/**
 * TwoUpSplitYoutube
 * - Stacks video (top) and YouTube video (bottom) vertically
 * - Can download YouTube videos as MP4 and display them natively
 * - Split is modular (50/50, 25/75, 60/40, etc.)
 * - White divider between tracks
 */
export const TwoUpSplitYoutube = ({
  topSrc = "",
  bottomYoutubeUrl = "",
  youtubeStartTime = 0, // Start time in seconds (optional)
  youtubeEndTime = 0,   // End time in seconds (optional)
  downloadedVideoPath = "", // Path to downloaded MP4 file
  useDownloadedVideo = true, // Whether to use downloaded video instead of iframe
  // split as number (top %) or string "25/75"
  split = 50,
  // video options (top only)
  topStartFrom = 0,
  topPlaybackRate = 1,
  topMuted = true,
  // video options for bottom video
  bottomStartFrom = 0,
  bottomPlaybackRate = 1,
  bottomMuted = true,
  // visuals
  borderColor = "#ffffff",
  borderWidth = 8, // px
  objectFit = "cover",
  // caption options
  caption = "",
  captionBgColor = "#ff0000ff",
  captionTextColor = "#ffffff",
  captionPadding = "8px 16px",
  captionFontSize = "80px",
  captionFontWeight = "600",
  captionFontFamily = "Arial, sans-serif",
  captionBorderRadius = "12px",
}) => {
  const [topVideoError, setTopVideoError] = useState(null);
  const [bottomVideoError, setBottomVideoError] = useState(null);


  // Helper function to convert time string (MM:SS or seconds) to seconds
  const parseTimeToSeconds = (time) => {
    if (typeof time === 'number') {
      return time;
    }
    
    if (typeof time === 'string') {
      // Handle MM:SS or HH:MM:SS format
      const timeParts = time.split(':').map(part => parseInt(part, 10));
      
      if (timeParts.length === 2) {
        // MM:SS format
        const [minutes, seconds] = timeParts;
        return (minutes * 60) + seconds;
      } else if (timeParts.length === 3) {
        // HH:MM:SS format
        const [hours, minutes, seconds] = timeParts;
        return (hours * 3600) + (minutes * 60) + seconds;
      } else if (timeParts.length === 1) {
        // Just seconds as string
        return timeParts[0];
      }
    }
    
    return undefined;
  };

  // Helper function to extract video ID from YouTube URL
  const getYouTubeVideoId = (url) => {
    if (!url || typeof url !== 'string') return null;
    const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#]+)/);
    return videoIdMatch ? videoIdMatch[1] : null;
  };

  // Helper function to get downloaded video path
  const getDownloadedVideoPath = () => {
    if (downloadedVideoPath) {
      return downloadedVideoPath;
    }
    
    // Try to construct path based on video ID
    const videoId = getYouTubeVideoId(bottomYoutubeUrl);
    if (videoId) {
      // Check common download locations
      const possiblePaths = [
        `youtube-downloads/${videoId}.mp4`,
        `public/youtube-downloads/${videoId}.mp4`,
        `downloads/${videoId}.mp4`,
        `public/downloads/${videoId}.mp4`,
      ];
      
      // For now, return the first one - in a real implementation you'd check if file exists
      return possiblePaths[0];
    }
    
    return null;
  };

  // Helper function to convert YouTube URL to embed URL with timing
  const getYouTubeEmbedUrl = (url, startTime, endTime) => {
    if (!url || typeof url !== 'string') return "";
    
    // If it's already an embed URL, return as is
    if (url.includes("youtube.com/embed/")) {
      return url;
    }
    
    // Extract video ID from various YouTube URL formats including Shorts
    const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#]+)/);
    if (videoIdMatch) {
      const videoId = videoIdMatch[1];
      
      // Build URL parameters
      const params = new URLSearchParams({
        autoplay: '1',
        mute: '1',
        loop: '1',
        playlist: videoId
      });
      
      // Add start time if provided (convert to seconds)
      const startSeconds = parseTimeToSeconds(startTime);
      if (typeof startSeconds === 'number' && startSeconds > 0) {
        params.set('start', Math.floor(startSeconds).toString());
      }
      
      // Add end time if provided (convert to seconds)
      const endSeconds = parseTimeToSeconds(endTime);
      if (typeof endSeconds === 'number' && endSeconds > 0) {
        params.set('end', Math.floor(endSeconds).toString());
      }
      
      return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
    }
    
    return url; // Return original if we can't parse it
  };

  const parseSplit = (s) => {
    if (typeof s === "number") return Math.max(0, Math.min(100, s));
    if (typeof s === "string" && s.includes("/")) {
      const [a, b] = s.split("/").map((n) => parseFloat(n));
      if (Number.isFinite(a) && Number.isFinite(b) && a + b > 0) {
        return (a / (a + b)) * 100;
      }
    }
    return 50;
  };

  const topPercent = parseSplit(split);
  const bottomPercent = 100 - topPercent;

  // Convert time parameters to seconds for Remotion
  const startSeconds = parseTimeToSeconds(youtubeStartTime);
  const endSeconds = parseTimeToSeconds(youtubeEndTime);
  const downloadedPath = useMemo(() => getDownloadedVideoPath(), [downloadedVideoPath, bottomYoutubeUrl]);

  const renderErrorFallback = (title, details) => {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#111",
          color: "#fff",
          textAlign: "center",
          padding: 24,
          fontFamily: "Arial, sans-serif",
        }}
      >
        <div style={{ fontSize: 24, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 14, color: "#b3b3b3", maxWidth: 760 }}>{details}</div>
      </div>
    );
  };

  return (
    <AbsoluteFill style={{backgroundColor: "transparent"}}>
      {/* Main video container */}
      <div
        style={{
          display: "grid",
          // 3 rows: top, border, bottom
          gridTemplateRows: `${topPercent}% ${borderWidth}px ${bottomPercent}%`,
          width: "100%",
          height: "100%",
          backgroundColor: "transparent",
        }}
      >
        {/* Top video */}
        <div style={{position: "relative", backgroundColor: "transparent", overflow: "hidden"}}>
          {topVideoError
            ? renderErrorFallback("Top video failed to load", topVideoError)
            : (
              <OffthreadVideo
                src={topSrc}
                startFrom={topStartFrom}
                playbackRate={topPlaybackRate}
                muted={topMuted}
                onError={(error) => {
                  const message = error?.error?.message || error?.message || "Unknown media playback error";
                  setTopVideoError(`${message} (src: ${topSrc})`);
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit,
                  backgroundColor: "transparent",
                }}
              />
            )}
        </div>

        {/* Divider */}
        <div
          style={{
            background: borderColor,
            width: "100%",
            height: borderWidth,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          {caption && (
            <div
              style={{
                backgroundColor: captionBgColor,
                color: captionTextColor,
                padding: captionPadding,
                fontSize: captionFontSize,
                fontWeight: captionFontWeight,
                fontFamily: captionFontFamily,
                borderRadius: captionBorderRadius,
                whiteSpace: "nowrap",
                zIndex: 10,
              }}
            >
              {caption}
            </div>
          )}
        </div>

        {/* Bottom Video - either downloaded MP4 or YouTube iframe */}
        <div style={{position: "relative", backgroundColor: "transparent", overflow: "hidden"}}>
          {useDownloadedVideo && downloadedPath ? (
            // Use downloaded MP4 video
            bottomVideoError
              ? renderErrorFallback("Bottom video failed to load", bottomVideoError)
              : (
                <OffthreadVideo
                  src={staticFile(downloadedPath)}
                  startFrom={startSeconds || bottomStartFrom}
                  endAt={endSeconds}
                  playbackRate={bottomPlaybackRate}
                  muted={bottomMuted}
                  onError={(error) => {
                    const message = error?.error?.message || error?.message || "Unknown media playback error";
                    setBottomVideoError(`${message} (src: ${downloadedPath})`);
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit,
                    backgroundColor: "transparent",
                  }}
                />
              )
          ) : (
            // Fallback to YouTube iframe
            <iframe
              src={getYouTubeEmbedUrl(bottomYoutubeUrl, youtubeStartTime, youtubeEndTime)}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
              allow="autoplay; encrypted-media;"
              allowFullScreen
            />
          )}
        </div>
      </div>
    </AbsoluteFill>
  );
};

export default TwoUpSplitYoutube;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
